// FIXME: A lot of this logic was taken from the old DPC, and needs to be cleaned up

import { symbolColors } from '../../../../types/constants/symbol-colors';
import { CoordinateApiModel } from '../../../../types/models/coordinate.model';

type Coordinate = CoordinateApiModel & { id?: string; name?: string; highlight?: boolean };

type Props = {
  coordinates: Coordinate[];
  width?: number;
  height?: number;
  opacity?: number;
  showPaddedViewbox?: boolean;
  onComponentClick?: (coordinate: CoordinateApiModel) => void;
  onHelperClick?: (coordinate: CoordinateApiModel | null) => void;
};

export const SymbolViewer = ({
  coordinates,
  width = 200,
  height = 200,
  opacity = 1,
  showPaddedViewbox,
  onComponentClick,
  onHelperClick,
}: Props) => {
  const useColor = coordinates[0]?.cx === 1;

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : null;
  };

  const getRGBA = (hex: string, alpha: number) => {
    if (useColor) return `rgba(${hexToRgb(hex)?.r},${hexToRgb(hex)?.g},${hexToRgb(hex)?.b},${alpha})`;
    return `rgba(0,0,0,${alpha})`;
  };

  const renderHelper = (obj: Coordinate) => {
    const { id, x1, y1 } = obj;
    let fill: string;
    switch (id) {
      case 'helper1':
        fill = 'rgb(47,255,0)';
        break;
      case 'helper2':
        fill = 'rgb(255,0,0)';
        break;
      case 'helper3':
        fill = 'rgb(153,0,255)';
        break;
      default:
        fill = 'rgb(255,166,0)';
        break;
    }
    return (
      <rect
        key={id}
        x={x1 - 1}
        y={y1 - 1}
        width="2"
        height="2"
        style={{ stroke: 'rgba(0,0,0,0)', strokeWidth: '1', fill }}
        onMouseDown={() => (onHelperClick ? onHelperClick(obj) : null)}
        onMouseUp={() => (onHelperClick ? onHelperClick(null) : null)}
      />
    );
  };

  const renderPoint = (obj: Coordinate, index: number) => {
    const { name, x1, y1, type, color, highlight } = obj;
    let stroke = getRGBA('#000000', opacity);
    if (highlight) {
      stroke = getRGBA(symbolColors[color], 1);
    } else if (useColor) {
      stroke = getRGBA(symbolColors[color], opacity);
    }

    return (
      <rect
        key={name + type + index}
        x={highlight ? x1 - 1 : x1 - 0.5}
        y={highlight ? y1 - 1 : y1 - 0.5}
        width={highlight ? 2 : 1}
        height={highlight ? 2 : 1}
        style={{
          stroke,
          strokeWidth: '1',
          fill: highlight ? 'rgb(48,255,0)' : `rgba(0,0,0,${opacity})`,
        }}
        onClick={() => (onComponentClick ? onComponentClick(obj) : null)}
      />
    );
  };

  const renderLine = (obj: Coordinate, index: number) => {
    const { name, x1, y1, x2, y2, type, color, highlight } = obj;
    let stroke = getRGBA('#000000', opacity);
    if (highlight) {
      stroke = getRGBA(symbolColors[color], 1);
    } else if (useColor) {
      stroke = getRGBA(symbolColors[color], opacity);
    }

    return (
      <line
        key={name + type + index}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        style={{ stroke, strokeWidth: '1', fill: 'none' }}
        onClick={() => (onComponentClick ? onComponentClick(obj) : null)}
      />
    );
  };

  const renderArc = (obj: Coordinate) => {
    const { x1, y1, x2, y2, cx, cy, color, largeArcSweep, highlight } = obj;
    let stroke = getRGBA('#000000', opacity);
    if (highlight) {
      stroke = getRGBA(symbolColors[color], 1);
    } else if (useColor) {
      stroke = getRGBA(symbolColors[color], opacity);
    }

    if (x1 === x2 && y1 === y2) {
      const dx = Math.abs(cx - x1);
      const dy = Math.abs(cy - y1);
      const radius = Math.sqrt(dx ** 2 + dy ** 2);
      return (
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          style={{ stroke, fill: 'none' }}
          onClick={() => (onComponentClick ? onComponentClick(obj) : null)}
        />
      );
    }

    const dx1 = Math.abs(cx - x1);
    const dy1 = Math.abs(cy - y1);
    const dx2 = Math.abs(cx - x2);
    const dy2 = Math.abs(cy - y2);
    const r1 = Math.sqrt(dx1 ** 2 + dy1 ** 2);
    const r2 = Math.sqrt(dx2 ** 2 + dy2 ** 2);
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    let sweep = false;

    // determine the sweep flag (if the arm is drawn counter-clockwise from start point to end point
    if (cx === mx && cy === my) sweep = largeArcSweep;
    else if (y1 === y2) {
      if (x1 < x2 && cy >= my && !largeArcSweep) sweep = true;
      else if (x1 > x2 && cy <= my && !largeArcSweep) sweep = true;
      else if (x1 < x2 && cy <= my && largeArcSweep) sweep = true;
      else if (x1 > x2 && cy >= my && largeArcSweep) sweep = true;
    } else if (x1 === x2) {
      if (y1 < y2 && cx <= mx && !largeArcSweep) sweep = true;
      else if (y1 > y2 && cx >= mx && !largeArcSweep) sweep = true;
      else if (y1 < y2 && cx >= mx && largeArcSweep) sweep = true;
      else if (y1 > y2 && cx <= mx && largeArcSweep) sweep = true;
    } else if (x1 < x2 && y1 < y2) {
      // up right
      if (cy >= my && !largeArcSweep) sweep = true;
      else if (cy <= my && largeArcSweep) sweep = true;
    } else if (x1 < x2 && y1 > y2) {
      // down right
      if (cy >= my && !largeArcSweep) sweep = true;
      else if (cy <= my && largeArcSweep) sweep = true;
    } else if (x1 > x2 && y1 < y2) {
      // up left
      if (cy <= my && !largeArcSweep) sweep = true;
      else if (cy >= my && largeArcSweep) sweep = true;
    } else if (x1 > x2 && y1 > y2) {
      // down left
      if (cy <= my && !largeArcSweep) sweep = true;
      else if (cy >= my && largeArcSweep) sweep = true;
    }

    return (
      <path
        d={`M${x1},${y1} A${r1},${r2} 0 ${Number(largeArcSweep)} ${Number(sweep)} ${x2},${y2}`}
        style={{ fill: 'none', stroke }}
        onClick={() => (onComponentClick ? onComponentClick(obj) : null)}
      />
    );
  };

  const svgDots = [];
  for (let i = 1; i < 20; i += 1) {
    for (let j = 1; j < 20; j += 1) {
      svgDots.push(
        <circle
          key={`dot,${i},${j}`}
          cx={i * 10}
          cy={j * 10}
          r="1"
          style={{ fill: 'rgba(0,0,0,0.03)', stroke: 'none' }}
        />,
      );
    }
  }

  return (
    <svg
      width={width}
      height={height}
      viewBox={showPaddedViewbox ? '-6 -6 212 212' : '0 0 200 200'}
      style={{ backgroundColor: '#f6f6f6' }}
    >
      <g transform="translate(0,200)">
        <g transform="scale(1,-1)">
          {svgDots}

          <line x1="100" y1="0" x2="100" y2="200" style={{ fill: 'none', stroke: 'rgba(0,0,0,0.03)' }} />
          <line x1="0" y1="100" x2="200" y2="100" style={{ fill: 'none', stroke: 'rgba(0,0,0,0.03)' }} />
          <rect x="-1" y="-1" width="202" height="202" style={{ fill: 'none', stroke: 'rgba(0,0,0,0.03)' }} />

          {coordinates.map((obj, index) => {
            if (obj.id === 'helper1' || obj.id === 'helper2' || obj.id === 'helper3' || obj.id === 'helper4') {
              return renderHelper(obj);
            }

            switch (obj.type) {
              case 'Point':
                return renderPoint(obj, index);
              case 'Line':
                return renderLine(obj, index);
              case 'Arc':
                return renderArc(obj);
              default:
                return null;
            }
          })}
        </g>
      </g>
    </svg>
  );
};
